import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import CompleteVerification from "./components/Auth/CompleteVerification";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ChangePassword from "./components/Auth/ChangePassword";
import PrivateRoute from "./components/Auth/PrivateRoute";
import NotFound from "./components/NotFound";
import EditProfile from "./components/Modals/EditProfile";
import { Box, CssBaseline } from "@mui/material";
import Mod from "./components/Tabs/Mod";
import Profile from "./components/Tabs/Profile";
import AdminDashboard from "./pages/Dashboard";
import UpdatePassword from "./components/Modals/UpdatePassword";
import WeeklyQA from "./components/Tabs/WeeklyQA";
import Meals from "./components/Tabs/Meals";
import Programs from "./components/Tabs/Programs";
import useThemeColor from "./utils/useTheme";
import Diary from "./components/Tabs/Diary";
import VerifyIdentity from "./components/Auth/VerifyIdentity";
import ManageBilling from "./components/Modals/ManageBilling";
import PointsStats from "./components/Modals/PointStats";
import CodeRedirect from "./components/Tabs/CodeRedirect";
import { useOrientation } from "react-use";
import useDateWithMidnightUpdate from "./utils/useDateWithMidnightHook";
import UnsubscribePage from "./components/Unsubscribe";
import WeightChart from "./components/Extra/WeightConvert";
import MessagesPage from "./pages/MessagesPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const scrollableElement = document.getElementById("dashboard-main"); // or document.body
    scrollableElement.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const DashboardLayout = () => {
  const [activeLevelForMobile, setActiveLevelForMobile] = useState(false);
  const [activeDiaryLevel, setActiveDiaryLevel] = useState(false);
  const [open, setOpen] = React.useState(true);
  const [openSubscriptionModal, setOpenSubscriptionModal] =
    React.useState(false);
  const [openFreeTrialModal, setOpenFreeTrialModal] = React.useState(false);
  const [isShowingAllStats, setIsShowingAllStats] = useState(false);
  const [isShowingLeaderBoard, setIsShowingLeaderBoard] = useState(false);
  const [isShowingCreateMeal, setIsShowingCreateMeal] = useState(false);
  const [isShowingCreateRecipe, setIsShowingCreateRecipe] = useState(false);
  const [isShowingCreateCustomFood, setIsShowingCreateCustomFood] =
    useState(false);
  const [isAddingMealItem, setIsAddingMealItem] = useState(false);
  const [editMealToDiary, setEditMealToDiary] = useState(false);
  const [editRecipeToDiary, setEditRecipeToDiary] = useState(false);
  const [editCustomFoodToDiary, setEditCustomFoodToDiary] = useState(false);
  const [editOneOffToDiary, setEditOneOffToDiary] = useState(false);
  const [isViewingUserMeals, setIsViewingUserMeals] = React.useState({});
  const [isViewingUserMealsMod, setIsViewingUserMealsMod] = React.useState({});
  const { data } = useDateWithMidnightUpdate("lastDiaryVisitedLevel");
  // console.log("updating data at", data);
  const lastDiaryVisited = localStorage.getItem("diaryLastVisited");
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oc = params.get("oc");

    // 🔹 If oc=1 → go to mention path and stop
    if (oc === "1") {
      navigate("/profile?oc=1", { replace: true });
      return;
    }

    // Check if we've been redirected due to day change (midnight)
    const redirectToMod = localStorage.getItem("redirectToMod");

    if (redirectToMod === "true") {
      // Clear the redirect flag
      localStorage.removeItem("redirectToMod");
      // Navigate to home on day change
      navigate("/");
      console.log("Day changed - redirecting to home page");
    } else {
      // Normal navigation logic - go to diary if previously visited, home otherwise
      const lastDiaryVisited = localStorage.getItem("diaryLastVisited");
      if (lastDiaryVisited) {
        navigate("/diary");
      }
    }
  }, [data.lastUpdated]);
  const styles = useThemeColor();
  return (
    <Box display="flex">
      <CssBaseline />
      <AdminDashboard
        openFreeTrialModal={openFreeTrialModal}
        setOpenFreeTrialModal={setOpenFreeTrialModal}
        openSubscriptionModal={openSubscriptionModal}
        setOpenSubscriptionModal={setOpenSubscriptionModal}
        open={open}
        setOpen={setOpen}
        setActiveLevelForMobile={setActiveLevelForMobile}
        setIsShowingAllStats={setIsShowingAllStats}
        setIsShowingLeaderBoard={setIsShowingLeaderBoard}
        setIsShowingCreateMeal={setIsShowingCreateMeal}
        setIsShowingCreateRecipe={setIsShowingCreateRecipe}
        setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
        setIsAddingMealItem={setIsAddingMealItem}
        setEditMealToDiary={setEditMealToDiary}
        setEditRecipeToDiary={setEditRecipeToDiary}
        setEditOneOffToDiary={setEditOneOffToDiary}
        setEditCustomFoodToDiary={setEditCustomFoodToDiary}
        setIsViewingUserMeals={setIsViewingUserMeals}
        setIsViewingUserMealsMod={setIsViewingUserMealsMod}
      />

      <Box
        component="main"
        id="dashboard-main"
        sx={{
          flexGrow: 1,
          color: styles.color,
          background: styles.background,
          padding: {
            xs: "0 0 115px 0",
            md: 0
          },
          height: "100dvh",
          overflow: "auto"
        }}
      >
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <Mod
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                isViewingUserMealsMod={isViewingUserMealsMod}
                setIsViewingUserMealsMod={setIsViewingUserMealsMod}
              />
            }
          />
          <Route
            path="/diary"
            element={
              <Diary
                isShowingAllStats={isShowingAllStats}
                setIsShowingAllStats={setIsShowingAllStats}
                isShowingLeaderBoard={isShowingLeaderBoard}
                setIsShowingLeaderBoard={setIsShowingLeaderBoard}
                isShowingCreateMeal={isShowingCreateMeal}
                setIsShowingCreateMeal={setIsShowingCreateMeal}
                isShowingCreateRecipe={isShowingCreateRecipe}
                setIsShowingCreateRecipe={setIsShowingCreateRecipe}
                isShowingCreateCustomFood={isShowingCreateCustomFood}
                setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
                isAddingMealItem={isAddingMealItem}
                setIsAddingMealItem={setIsAddingMealItem}
                editMealToDiary={editMealToDiary}
                setEditMealToDiary={setEditMealToDiary}
                editRecipeToDiary={editRecipeToDiary}
                setEditRecipeToDiary={setEditRecipeToDiary}
                editCustomFoodToDiary={editCustomFoodToDiary}
                setEditCustomFoodToDiary={setEditCustomFoodToDiary}
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                editOneOffToDiary={editOneOffToDiary}
                setEditOneOffToDiary={setEditOneOffToDiary}
                activeLevelForMobile={activeDiaryLevel}
                setActiveLevelForMobile={setActiveDiaryLevel}
              />
            }
          />
          <Route
            path="/programs"
            element={
              <Programs
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                open={open}
                activeLevelForMobile={activeLevelForMobile}
                setActiveLevelForMobile={setActiveLevelForMobile}
              />
            }
          />
          <Route
            path="/meals"
            element={
              <Meals
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                isViewingUserMeals={isViewingUserMeals}
                setIsViewingUserMeals={setIsViewingUserMeals}
              />
            }
          />
          <Route
            path="/profile"
            element={
              <Profile
                openSubscriptionModal={openSubscriptionModal}
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                setOpenFreeTrialModal={setOpenFreeTrialModal}
              />
            }
          />
          <Route
            path="/edit-profile"
            element={
              <EditProfile
                setOpenSubscriptionModal={setOpenSubscriptionModal}
              />
            }
          />
          <Route path="/weight-stats" element={<WeightChart />} />
          <Route
            path="/point-stats"
            element={
              <PointsStats
                setOpenSubscriptionModal={setOpenSubscriptionModal}
              />
            }
          />
          <Route
            path="/update-password"
            element={
              <UpdatePassword
                setOpenSubscriptionModal={setOpenSubscriptionModal}
              />
            }
          />
          <Route
            path="/qa"
            element={
              <WeeklyQA
                setOpenSubscriptionModal={setOpenSubscriptionModal}
                isViewingUserMeals={isViewingUserMeals}
                setIsViewingUserMeals={setIsViewingUserMeals}
              />
            }
          />
          <Route
            path="/manage-billing"
            element={
              <ManageBilling
                setOpenSubscriptionModal={setOpenSubscriptionModal}
              />
            }
          />
          <Route
            path="/messages"
            element={<MessagesPage />}
          />
        </Routes>
      </Box>
    </Box>
  );
};

const BlockMobileLandscape = () => (
  <Box
    display={"flex"}
    flexDirection={"column"}
    alignItems={"center"}
    justifyContent="center"
  >
    <h2>Rotate Your Device</h2>
    <p>This app is not available in mobile landscape mode.</p>
  </Box>
);
function App() {
  const { type } = useOrientation();
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  useEffect(() => {
    const checkDeviceType = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isLandscape = type.includes("landscape");

      // A mobile is typically a device with an aspect ratio > 0.65 and a smaller height
      const isMobile = Math.min(screenWidth, screenHeight) < 500; // Adjusting for large phones

      setIsMobileLandscape(isMobile && isLandscape);
    };

    checkDeviceType();
    window.addEventListener("resize", checkDeviceType);
    return () => window.removeEventListener("resize", checkDeviceType);
  }, [type]);

  if (isMobileLandscape && window.location.pathname !== "/programs") {
    return <BlockMobileLandscape />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-identity" element={<VerifyIdentity />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route
          path="/complete-registeration"
          element={<CompleteVerification />}
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ChangePassword />} />
        <Route path="/r/:code?" element={<CodeRedirect />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
